import React, { useEffect } from 'react';
import { Calculator, ChevronDown, ChevronRight, DollarSign, Eye, EyeOff, Lock, Unlock } from 'lucide-react';
import LineRow from './pacing-table-line-row';
import DateRangeModal from './date-range-modal';
import { formatDateForDisplay, onPresetDateSelect } from '../utils/date-utils';
import { CampaignNode, presetPacingRanges } from '../types/pacing.types';
import { PacingViewApiResponse } from '../endpoints/pacing.types';
import { useUpdateLineItemMutation } from '../endpoints/pacing.endpoints';
import { computeStatus, to2dp } from '../utils/pacing-utils';

interface CampaignRowProps {
  campaign: CampaignNode;
  isLastRow: boolean;
  view?: PacingViewApiResponse;
  showAll: boolean;
}

const CampaignRow: React.FC<CampaignRowProps> = ({ campaign, isLastRow, view, showAll }) => {
  const [dateLabel, setDateLabel] = React.useState(`${formatDateForDisplay(new Date(campaign.agg.start_date ?? ''))} - ${formatDateForDisplay(new Date(campaign.agg.end_date ?? ''))}`);
  const [dateFilter, setDateFilter] = React.useState('last_week');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [budget, setBudget] = React.useState(campaign.agg.budget.toLocaleString());
  const [dailyBudget, setDailyBudget] = React.useState(campaign.agg.daily_budget.toLocaleString());
  const [campaignStatus, setCampaignStatus] = React.useState(campaign.agg.status);
  const [isVisible, setIsVisible] = React.useState(campaign.agg.is_visible == undefined ? true : campaign.agg.is_visible);
  const [isLocked, setIsLocked] = React.useState(campaign.agg.is_locked == undefined ? false : campaign.agg.is_locked);

  useEffect(() => {
    const daysRemaining = Math.ceil((new Date(campaign.agg.end_date ?? '').getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    setCampaignStatus(computeStatus(daysRemaining, campaign.agg.daily_budget, campaign.agg.spend, campaign.agg.budget));
    setIsVisible(campaign.agg.is_visible == undefined ? true : campaign.agg.is_visible);
    setIsLocked(campaign.agg.is_locked == undefined ? false : campaign.agg.is_locked);
  }, [campaign.agg.status, campaign.agg.daily_budget, campaign.agg.spend, campaign.agg.budget,
    campaign.agg.end_date, campaign.agg.start_date, campaign.agg.is_visible, campaign.agg.is_locked]);

  const [updateLineItem, { isLoading: isUpdating }] = useUpdateLineItemMutation();

  useEffect(() => {
    setDateLabel(() => {
      const startDate = formatDateForDisplay(new Date(campaign.agg.start_date + 'T00:00:00')); // Force local time
      const endDate = formatDateForDisplay(new Date(campaign.agg.end_date + 'T00:00:00'));
      return `${startDate} - ${endDate}`;
    });
  }, [campaign.agg.start_date, campaign.agg.end_date]);

  useEffect(() => {
    setBudget(campaign.agg.budget.toLocaleString());
    setDailyBudget(campaign.agg.daily_budget.toLocaleString());
  }, [campaign.agg.budget, campaign.agg.daily_budget]);

  const handleUpdateCampaignItem = (field: string, value: any) => {
    if (view?.id) {
      // let remainingBudget = Number(value ?? 0);
      for (const line of campaign.line_items) {
        let _value = 0;

        if (field === 'budget' && value > 0) {
          _value = Number((value / campaign.line_items.length).toFixed(4));
        }

        if (field === 'daily_budget' && value > 0) {
          _value = Number((value / campaign.line_items.length).toFixed(4));
        }

        if (field === 'is_locked' || field === 'is_visible') {
          _value = value;
        }

        updateLineItem({ lineItem: line, field, value: _value, viewId: view.id });
      }
    }
  };

  const handleCalculateDailyBudget = () => {
    const daysRemaining = Math.ceil((new Date(campaign.agg.end_date ?? '').getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    const spendRemaining = Number(campaign.agg.budget ?? 0) - Number(campaign.agg.spend ?? 0);
    const value = spendRemaining / Number(daysRemaining ?? 0);

    handleUpdateCampaignItem('daily_budget', value > 0 ? value.toFixed(2) : 0);
    setDailyBudget(value > 0 ? value.toFixed(2) : '0');
  };


  const handleUpdateCampaignItemDates = (filter: string, startDate: string, endDate: string) => {
    if (view?.id) {
      for (const line of campaign.line_items) {
        updateLineItem({ lineItem: line, field: 'start_date', value: startDate, viewId: view.id });
        updateLineItem({ lineItem: line, field: 'end_date', value: endDate, viewId: view.id });
      }
    }
  };

  if (!showAll && !isVisible) {
    return null;
  }

  return (
    <>
      <tr className={`max-w-1/4 bg-gray-200 !border-l-yellow-500 !border-r-yellow-500 border-l-4 border-r-4 ${isLastRow && !isExpanded ? '!border-b-yellow-500 !border-b-4' : 'border-b-1 border-t-0 border-gray-400'}`}>
        <td className='w-[3%] min-w-[15px] border-r border-gray-201 items-center justify-center'>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="hover:bg-gray-100 rounded p-1"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        </td>
        <td className="w-[30%] px-6 py-1 whitespace-nowrap border-r border-gray-201">
          <div className='flex flex-col items-left'>

            <div className='flex items-center'>
              <div className="text-sm font-medium text-gray-900">
                {campaign.name}
                {/* <span className='text-xs text-gray-500 ml-2'>
                  {line.account.source?.replace('_', ' ').toLowerCase()}
                </span> */}
              </div>
              <div>
                {showAll && (
                  <div className='flex items-center gap-2'>
                    <div
                      onClick={(e) => {e.stopPropagation(); setIsVisible(!isVisible); handleUpdateCampaignItem('is_visible', !isVisible);}}
                      className='cursor-pointer ml-2'
                    >
                      {isVisible ? <Eye className="h-4 w-4 bg-blue-500 hover:bg-blue-400 text-white rounded-sm p-0.5 cursor-pointer" /> : <EyeOff className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                    </div>
                    <div
                      onClick={(e) => {e.stopPropagation(); setIsLocked(!isLocked); handleUpdateCampaignItem('is_locked', !isLocked);}}
                      className='cursor-pointer'
                    >
                      {isLocked ? <Lock className="h-4 w-4 bg-orange-501 text-white rounded-sm hover:bg-orange-300 p-0.5 cursor-pointer" /> : <Unlock className="h-4 w-4 bg-gray-201 text-black hover:bg-gray-200 rounded-sm p-0.5 cursor-pointer" />}
                    </div>
                  </div>
                )}
              </div>

            </div>
            <div className='flex items-center text-xs text-gray-500'>
              <div
                className={`w-2 h-2 rounded-full mr-1 ${campaignStatus === 'on_track' ? 'bg-green-501' : campaignStatus === 'ahead' ? 'bg-red-501' : campaignStatus === 'behind' ? 'bg-yellow-500' : 'bg-gray-500'}`}
              />
              {campaignStatus.toLocaleLowerCase().replace('_', ' ')}
              <div className='w-full ml-4 bg-gray-400 rounded-full h-2 text-center'>
                <div
                  className='bg-blue-500 h-2 rounded-full text-gray-200 items-center justify-center'
                  style={{ width: `${Math.min(campaign?.agg.percent_complete ?? 0, 100).toFixed(0)}%` }}
                />
              </div>
            </div>

          </div>

        </td>
        <td className="w-[100px] px-4 py-1 whitespace-nowrap border-r border-gray-201">
          <div
            className="flex justify-center items-center text-sm text-gray-900 font-bold"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            <input
              type="text"
              inputMode="decimal"
              pattern="^\d+(\.\d{1,2})?$"
              value={budget}
              onChange={(e) => {setBudget(to2dp(e.target.value));}}
              onBlur={(e) => {handleUpdateCampaignItem('budget', e.target.value);}}
              className="w-[50px] mr-2 font-bold text-sm text-gray-900 bg-gray-200 focus:outline-none border-b-1 border-gray-201"
            />
          </div>
        </td>
        <td className="px-4 py-1 whitespace-nowrap border-r rounded-none border-gray-201">
          <div
            className="flex justify-between items-center text-sm text-gray-900 font-bold"
          >
            <DollarSign className="w-4 h-4 mr-1" />
            <input
              type="text"
              inputMode="decimal"
              pattern="^\d+(\.\d{1,2})?$"
              value={dailyBudget}
              onChange={(e) => {setDailyBudget(to2dp(e.target.value));}}
              onBlur={(e) => {handleUpdateCampaignItem('daily_budget', e.target.value);}}
              className="w-[70px] mr-2 font-bold text-sm text-gray-900 bg-gray-200 focus:outline-none border-b-1 border-gray-201"
            />

            <button
              onClick={() => handleCalculateDailyBudget()}
              className='text-black rounded-full p-1'
            >
              <Calculator className="w-4 h-4" />
            </button>
          </div>
        </td>
        <td className="px-6 py-1 whitespace-nowrap border-r border-gray-201">
          <div className="text-sm text-gray-900 font-bold">
            $
            {campaign.agg.spend.toFixed(2)}
          </div>
        </td>

        <td className="pr-4 pl-2 py-1 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            <DateRangeModal
              disclaimer='Note: Start Dates can only go back a maximum of 45 days'
              selectedDate={new Date(campaign.agg.start_date ?? '').toLocaleDateString()}
              onDateChange={(filter, startDate, endDate) => handleUpdateCampaignItemDates(filter, startDate, endDate)}
              onPresetSelect={(preset) => onPresetDateSelect(preset)}
              className={'text-sm font-bold'}
              selectedClassName={''}
              label={dateLabel}
              presetRanges={presetPacingRanges}
              selectedStartDate={campaign.agg.start_date ?? ''}
              selectedEndDate={campaign.agg.end_date ?? ''}
            />
          </div>
        </td>
      </tr>
      {isExpanded && campaign.line_items.map((line, index) => (
        <LineRow
          key={line.id}
          line={line}
          isLastRow={isLastRow && index === campaign.line_items.length - 1}
          view={view}
          showAll={showAll}
        />
      ))}
    </>
  );
};

export default CampaignRow;
